import VueRouter from 'vue-router';
import { magic } from '@gig/magic-sdk';
import { modalService } from '@gig/magic-common';
import { RouteName } from '../../../routes/routes.constants';
import { IUserProfile } from '@gig/magic-sdk/dist/adapter/users/user';
import {
    Feature,
    FEATURE_QUERY_ENABLED,
    USER_PROFILE_DATA,
} from '@gig/magic-messages';
/**
 * Guard to check if user has restrictions
 * @param router
 */
export enum CorexUserRestriction {
    DEPOSIT = 'deposit',
    WITHDRAW = 'withdraw',
    PROMOTIONS = 'promotions',
    WAGERING = 'wagering',
    SWEEPX_DEPOSIT = 'sweepx_deposit',
    SWEEPX_REDEEM = 'sweepx_redeem',
}
export function initUserRestrictionsGuard(router: VueRouter) {
    router.beforeEach(async (to, from, next) => {
        if (!magic.session.getUser() || to.name === RouteName.LOGOUT) {
            return next();
        }

        const restrictionType =
            typeof to.meta.corexUserRestriction === 'function'
                ? to.meta.corexUserRestriction(to)
                : to.meta.corexUserRestriction;

        if (restrictionType) {
            try {
                const user = await window.queryBus.query<IUserProfile>(
                    USER_PROFILE_DATA,
                );
                if (!user) {
                    return next();
                }

                const restrictionCheck = getRestrictionCheck(
                    user,
                    restrictionType,
                );

                const isFeatureEnabled = await window.queryBus.query<
                    string,
                    boolean
                >(
                    FEATURE_QUERY_ENABLED,
                    Feature.USER_RESTRICTIONS_CUSTOM_MODAL,
                );

                if (restrictionCheck.condition && !isModalOpen()) {
                    const modalOptions = {
                        dismissible: !isFeatureEnabled,
                        dismissibleBackdrop: false,
                        displayOnTop: true,
                        keepAfterClear: true,
                    };

                    openModal(restrictionCheck.content, modalOptions);
                    return next({ name: RouteName.HOME });
                }
            } catch (error) {
                console.error('Error checking user restrictions:', error);
                return next();
            }
        }

        next();
    });
}

export function getRestrictionCheck(
    user: IUserProfile,
    restrictionType: CorexUserRestriction,
): { condition: boolean; content: string } {
    switch (restrictionType) {
        case CorexUserRestriction.DEPOSIT:
            return {
                condition: !user.compliance.isDepositAllowed,
                content: 'deposit-restricted-modal.description',
            };
        case CorexUserRestriction.WITHDRAW:
            return {
                condition: !user.compliance.isWithdrawAllowed,
                content: 'withdraw-restricted-modal.description',
            };
        case CorexUserRestriction.PROMOTIONS:
            return {
                condition: !user.compliance.isPromotionsAllowed,
                content: 'promotions-restricted-modal.description',
            };
        case CorexUserRestriction.WAGERING:
            return {
                condition: !user.compliance.isWageringAllowed,
                content: 'wagering-restricted-modal.description',
            };
        default:
            return { condition: false, content: '' };
    }
}

export function openModal(
    content: string,
    modalOptions = {
        dismissible: true,
        dismissibleBackdrop: false,
        displayOnTop: true,
        keepAfterClear: true,
    },
) {
    const modal = document.createElement('magic-user-restrictions-modal');
    const modalHeader = 'user-restrictions-modal.title';
    modal.setAttribute('content', content);

    modalService.open(modalHeader, modal, modalOptions);
}

export function isModalOpen() {
    return (
        document.getElementsByTagName('magic-user-restrictions-modal')
            .length !== 0
    );
}
